<template>
    <modal :show="show"
        :title="modalTitle"
        :saveButton="{
            title:'Exportar',
            btnClass: {'btn btn-main btn-rounded btn-sm z-depth-0': true},
            visible: visibleExportBtn
        }"
        :cancelButton="{
            title: textCancelBtn,
            btnClass: {
                'btn btn-outline-main btn-rounded btn-outline btn-sm z-depth-0': true,
                'disabled': disabledCancelBtn
            }
        }"
        size="small"
        :needHeader="false"
        :relativeBackdrop="true"
        :contentCentered="true"
        class="modal-export"
        @close="onCancel"
        @save="onExportData"
    >
        <form class="dg-form"
              autocomplete="off"
              @submit.prevent=""
        >
            <div v-if="confirmExport && !isStartedJob">
                <p v-html="$t(confirmMessage)"></p>
            </div>
            <div v-if="!confirmExport || (confirmExport && isStartedJob)">
                <label for="dg-label" v-if="labelText" v-html="labelText"></label>
                <div class="progress" v-if="isStartedJob && !isCompletedJob">
                    <div
                        role="progressbar"
                        class="progress-bar"
                        :class="[progressClasses]"
                        :aria-valuenow="progress.current"
                        :aria-valuemin="progress.min"
                        :aria-valuemax="progress.max"
                        :style="styleObject">
                        {{ progressLabel }}
                    </div>
                </div>
                <div class="report-result" v-else>
                    <div v-if="hasUrl">
                        <a class="btn"
                            :class="[buttonColorClass]"
                            :href="urlReport"
                            role="button"
                            target="_blank">
                            {{ $t(downloadReportText) }}
                        </a>
                    </div>
                    <div v-if="hasFiles">
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item"
                                v-for="(file, fIdx) in filesReport" :key="`file-${fIdx}`">
                                <a target="_blank" :href="file[propertyFileUrl]">
                                    {{ file[propertyFileLabel] || defaultFileName }}
                                    <i v-if="showIconExport" class="fas fa-download"></i>
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div v-if="hasErrors">
                        <div
                            class="alert alert-danger m-0 mt-3 py-2 px-3 small"
                            role="alert">
                            <p v-for="(error, index) in errorsJob"
                                :key="index"
                                class="pb-0"
                                v-html="$sanitize(error)">
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </modal>
</template>

<script>
import Modal from './Modal';
import GlobalMixin from '@/app/shared/mixins/global';
import { jobStates } from '@/app/shared/components/workflow/config/job-states';
import { isArray, isObject } from '@config/utils';

export default {
    name: 'ModalExport',
    mixins: [
        GlobalMixin,
    ],
    components: {
        Modal
    },
    props: {
        title: {
            type: String,
        },
        progressAnimated: {
            type: Boolean,
            default: true,
        },
        progressStriped: {
            type: Boolean,
            default: true,
        },
        progressColorClass: {
            type: String,
            default: 'bg-info',
        },
        showProgressLabel: {
            type: Boolean,
            default: true,
        },
        buttonColorClass: {
            type: String,
            default: 'btn-info',
        },
        fileName: {
            type: String,
            default: `global.model.export.file`,
        },
    },
    data() {
        return {
            loading: false,
            show: false,
            modalTitle: null,
            helpText: null,
            finalText: null,
            confirmExport: false,
            confirmMessage: 'Pretende exportar os resultados?',
            downloadReportText: 'Download do Ficheiro',
            itemId: null,
            url: null,
            options: {},
            callback: null,
            itemData: null,
            progress: {
                min: 0,
                max: 100,
                current: 100,
            },
            job: {
                ms: 2 * 1000, //2s
                id: null,
                canceled: false,
                status: null,
                progress: null,
                output: null,
            },
            propertyUrl: 'url',
            propertyFiles: 'files',
            propertyFileLabel: 'label',
            propertyFileUrl: 'url',
            showIconExport: false,
        }
    },
    mounted() {
        this.modalTitle = this.title;
    },
    computed: {
        defaultFileName() {
            return this.$t(this.fileName);
        },
        visibleExportBtn() {
            if(this.confirmExport){
                return !this.isStartedJob;
            }else{
                return false;
            }
        },
        labelText() {
            return this.$t(this.isCompletedJob ? this.finalText : this.helpText);
        },
        textCancelBtn() {
            return this.$t(this.visibleExportBtn ? 'global.cancel' : 'global.close');
        },
        disabledCancelBtn() {
            if(this.confirmExport){
                return this.isStartedJob && !this.isCompletedJob;
            }else{
                return !this.isCompletedJob;
            }
        },
        progressLabel() {
            return this.showProgressLabel ?
                    (!this.isStartedJob || this.isWaitingJob || (this.isStartedJob && !this.isFinishedJob && this.currentValue == 100 && this.maxValue == 100) ?  '' : `${this.currentPercentage}%`) : '';
        },
        progressClasses() {
            return [
                this.progressColorClass,
                this.progressStriped ? 'progress-bar-striped' : '',
                this.progressAnimated && !this.isCompletedJob ? 'progress-bar-animated' : '',
            ];
        },
        currentValue() {
            return this.progress.current;
        },
        maxValue() {
            return this.progress.max;
        },
        currentPercentage() {
            return Math.round((this.progress.max != 0 ? (this.progress.current / this.progress.max) : 0) * 100);
        },
        styleObject() {
            return {
                width: `${this.currentPercentage}%`,
            }
        },
        isWaitingJob(){
            return this.job.status === jobStates.Waiting;
        },
        isFinishedJob(){
            return this.job.status === jobStates.Finished;
        },
        isFailedJob(){
            return this.job.status === jobStates.Failed;
        },
        isCanceledJob(){
            return this.job.canceled;
        },
        isStartedJob(){
            return this.job.status!==null;
        },
        isCompletedJob(){
            return this.isFinishedJob || this.isFailedJob  || this.isCanceledJob;
        },
        urlReport() {
            return this.job?.output?.[this.propertyUrl];
        },
        hasUrl() {
            return !!this.urlReport;
        },
        filesReport() {
            let files = this.job?.output?.[this.propertyFiles] || [];
            return isArray(files) ? files :
                Object.entries(Object.fromEntries(Object.entries(files).filter(([_, v]) => v != null)))
                    .reduce((newObj, [key, val]) => {return [...newObj, ...(isArray(val) ? val : [val])];}, []);
        },
        hasFiles() {
            return this.filesReport && this.filesReport.length>0;
        },
        errorsJob() {
            if(this.job.output && this.job.output.errors){
                let errors = this.job.output.errors;
                if(!isArray(errors)){
                    errors = [errors];
                }
                return errors;
            }
            return [];
        },
        hasErrors() {
            return this.errorsJob.length > 0;
        },
    },
    methods: {
        open: function(params) {
            this.reset();

            this.show=true;
            this.itemId = params.itemId;
            this.urlExport = params.urlExport;
            this.urlJob = params.urlJob;
            this.options = params.options || {};
            this.helpText = params.text;
            this.finalText = params.finalText || this.helpText;
            this.callback = params.callback;
            this.itemData = params.data || null;

            if(params.title != undefined){
                this.modalTitle = params.title;
            }
            if(params.confirmExport != undefined){
                this.confirmExport = params.confirmExport;
            }
            if(params.confirmMessage != undefined){
                this.confirmMessage = params.confirmMessage;
            }else{
                this.$data.confirmMessage = this.$options.data().confirmMessage;
            }
            if(params.downloadReportText != undefined){
                this.downloadReportText = params.downloadReportText;
            }else{
                this.$data.downloadReportText = this.$options.data().downloadReportText;
            }
            if(params.propertyUrl != undefined){
                this.propertyUrl = params.propertyUrl;
            }
            if(params.propertyFiles != undefined){
                this.propertyFiles = params.propertyFiles;
            }
            if(params.showIconExport != undefined){
                this.showIconExport = params.showIconExport;
            }

            if(!this.confirmExport){
                this.onExportData();
            }
        },
        closeModal: function() {
            this.show=false;
        },
        onExportData: function() {
            if(this.urlExport){
                this.reset();

                if(!this.options.method) {
                    this.options.method = isObject(this.urlExport) ? this.urlExport?.method : 'get';
                }

                this.$http({
                    ...this.options,
                    url: isObject(this.urlExport) ? this.urlExport?.url : this.urlExport,
                })
                .then(res => {
                    let data = res.data ? (res.data.data ? res.data.data : res.data) : res;
                    if(data.jobId){
                        if(this.urlJob){
                            let jobId = data.jobId;
                            this.waitingProcess(jobId);
                        }
                    }else{
                        this.job.status = jobStates.Finished;
                        this.job.output = data;
                    }
                })
                .finally(() => {
                })
                .catch((error) => {
                    this.showError(error);
                    this.cancelJob();
                })
            }
        },
        waitingProcess: function(id) {
            this.job.id = id;
            let ms = this.job.ms;

            const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

            // Initial state
            this.job.status = jobStates.Waiting;

            // Retrieve the initial operation status
            const getStatus = async (id) => {
                try {
                    const resp = await this.$http.get(this.urlJob.replace(':id', id));
                    let job = resp.data && resp.data.data ? resp.data.data : null;
                    this.job.status = job.status;
                    if(!this.isWaitingJob){
                        this.progress.max = job.max == 0 ? 100 : job.max;
                        this.progress.current = job.current == 0 ? this.progress.max : job.current;
                    }
                    // this.job.progress = job.output && job.output.progress ? (isArray(job.output.progress) ? job.output.progress.reverse() : job.output.progress) : null;

                    // Loop while the operation is still in progress...
                    if(!this.isCompletedJob) {
                        await delay(ms);
                        getStatus(id);
                    }else{
                        this.job.output = job.output;
                    }
                } catch (error) {
                    this.showError(error);
                    this.cancelJob();
                }
            };
            getStatus(this.job.id);
        },
        async cancelJob(){
            this.closeModal();
            await new Promise((resolve, reject) => {
                this.job.canceled = true;
                // wait
                setTimeout(() => resolve(), this.job.ms*1.1);
            });
        },
        resetJob: function(){
            Object.assign(this.$data.job, this.$options.data().job);
        },
        reset: function(){
            this.resetJob();
        },
        onCancel: function() {
            if(this.disabledCancelBtn){
                return;
            }
            this.cancelJob();
        },
        isArray: function(a) {
            return isArray(a);
        }
    }
}
</script>

<style scoped lang="scss">
    .progress {
        height: 20px
    }
    .list-group {
        .list-group-item {
            i {
                padding-left: 5px;
            }
        }
    }
</style>